import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import AuthLayout from '@components/auth/AuthLayout';
import { recoverPassword } from '@apis/users';

/**
 * Forgot password form state
 */
type ForgotPasswordForm = {
  username: string;
  email: string;
};

const INPUT_HEIGHT = 48;

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<ForgotPasswordForm>({
    username: '',
    email: '',
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  /**
   * Form validation (visual-only)
   */
  const canSubmit = useMemo(() => {
    return (
      form.username.trim().length > 0 &&
      form.email.trim().length > 0 &&
      !loading
    );
  }, [form.username, form.email, loading]);

  const handleChange =
    (key: keyof ForgotPasswordForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  /**
   * Request a temporary password.
   *
   * Note:
   * - Error messages are intentionally generic to avoid user enumeration.
   * - Backend is expected to normalize responses where possible.
   */
  const handleSubmit = useCallback(async () => {
    setErrorMsg(null);
    setLoading(true);

    try {
      const { message } = await recoverPassword(form);

      if (message !== 'OK') {
        setErrorMsg(message || 'Failed to recover password. Please try again.');
        return;
      }

      setSuccessOpen(true);
    } catch (e: any) {
      setErrorMsg(e?.message ?? 'Failed to recover password. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [form]);

  return (
    <>
      <AuthLayout
        title="Forgot password"
        subtitle="Enter your username and email. We’ll send a temporary password."
        footer={
          <Typography variant="caption" color="text.secondary">
            Authorized access only.
          </Typography>
        }
      >
        <Stack spacing={3}>
          {/* Error message */}
          {errorMsg && (
            <Alert severity="error">
              {errorMsg}
            </Alert>
          )}

          {/* Recovery form */}
          <Stack spacing={2}>
            <TextField
              label="Username"
              value={form.username}
              onChange={handleChange('username')}
              autoComplete="username"
              fullWidth
              disabled={loading}
              InputProps={{ sx: { height: INPUT_HEIGHT } }}
            />

            <TextField
              label="Email"
              value={form.email}
              onChange={handleChange('email')}
              autoComplete="email"
              fullWidth
              disabled={loading}
              InputProps={{ sx: { height: INPUT_HEIGHT } }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) {
                  handleSubmit();
                }
              }}
            />

            {/* Primary CTA */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={!canSubmit || loading}
              onClick={handleSubmit}
              sx={{ height: INPUT_HEIGHT, fontWeight: 700 }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Send temporary password'
              )}
            </Button>

            {/* Secondary action */}
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate('/login')}
              sx={{ fontSize: 14, alignSelf: 'flex-start' }}
              disabled={loading}
            >
              Back to sign in
            </Link>
          </Stack>
        </Stack>
      </AuthLayout>

      {/* Success dialog */}
      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Temporary password sent</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            A temporary password has been sent to your email.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setSuccessOpen(false);
              navigate('/login');
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ForgotPassword;
